import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { LicenseInfo } from '@mui/x-data-grid-pro';

import { configureStore } from '@reduxjs/toolkit';
import { Global } from '@emotion/react';
import { theme } from 'library';

// RTK Querys
import { setupListeners } from '@reduxjs/toolkit/query';
import dispatchingApi from 'feature/dispatching/dispatchingApi';
import { inventoryManagementApi } from 'feature/inventory-management/inventoryManagementApi';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import shippingApi from 'feature/shipping/shippingApi';
import timsApi from 'feature/loading-management/terminal-information/terminal-locator/timsApi';
import subscriptionApi from 'feature/subscription-plans/subscriptionApi';

import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import GlobalConfirmationDialog from 'common/confirmation-dialog/GlobalConfirmationDialog';
import { AuthProvider } from './authentication/AuthContext';
import SnackbarProvider from './snackbar/SnackbarProvider';
import reducer from './store/reducers';
import AppRoutes from './routing/AppRoutes';

import * as serviceWorkerRegistration from './serviceWorkerRegistration';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE);

const store = configureStore({
  // getDefaultMiddleware is deprecated in new redux toolkit
  // it now returns as a callback func to middleware
  // without getDefaultMiddleware the application will crash
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    })
      .concat(inventoryManagementApi.middleware)
      .concat(loadingManagementApi.middleware)
      .concat(dispatchingApi.middleware)
      .concat(shippingApi.middleware)
      .concat(timsApi.middleware)
      .concat(subscriptionApi.middleware),
  reducer,
});

setupListeners(store.dispatch);

const root = ReactDOM.createRoot(document.getElementById('root'));

const renderApp = async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.REACT_APP_LD_CLIENT_SIDE_ID,
  });
  root.render(
    <Provider store={store}>
      <LDProvider>
        <AuthProvider>
          <SnackbarProvider>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Global
                  styles={{
                    '&.SnackbarContainer-bottom.SnackbarContainer-left.SnackbarContainer-root': {
                      alignItems: 'unset',
                      maxWidth: 'calc(100% - 32px)',
                      width: '100%',
                    },
                    '@media(min-width: 600px)': {
                      '&.SnackbarContainer-bottom.SnackbarContainer-left.SnackbarContainer-root': {
                        maxWidth: 'calc(100% - 40px)',
                      },
                    },
                    '@media(min-width: 657px)': {
                      '&.SnackbarContainer-bottom.SnackbarContainer-left.SnackbarContainer-root': {
                        maxWidth: 600,
                      },
                    },
                    body: {
                      backgroundColor: theme.palette.surface.light,
                      fontSize: 14,
                      letterSpacing: 0.25,
                    },
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Router>
                    <AppRoutes />
                    <GlobalConfirmationDialog />
                  </Router>
                </LocalizationProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </SnackbarProvider>
        </AuthProvider>
      </LDProvider>
    </Provider>
  );
};

renderApp();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
  onUpdate: registration => {
    // eslint-disable-next-line no-console
    // console.log('Service Worker: Updating....');
    // Immediately skip waiting and activate new service worker
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    // Dispatch custom event when update is available
    // eslint-disable-next-line no-console
    console.log(
      'Service Worker: New content is available; please refresh. Firing serviceWorkerUpdate event.'
    );
    const event = new Event('serviceWorkerUpdate');
    window.dispatchEvent(event);
  },
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.register();
